import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField } from "@/lib/crm";
import { ITV_TYPES, typeLabel } from "@/lib/interventions";
import { fmtEur } from "@/lib/reports";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SCOPES = [{ key: "standard", label: "Standard" }, { key: "tipologia", label: "Per tipologia intervento" }, { key: "cliente", label: "Per cliente" }];
const today = () => new Date().toISOString().slice(0, 10);

const CompanyPick = ({ value, onPick }) => {
  const [q, setQ] = useState(""), [res, setRes] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setRes([]); return undefined; }
    const t = setTimeout(() => api.get("/crm/companies", { params: { q, limit: 8 } }).then((r) => setRes(r.data.items)).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative space-y-1.5"><Label className="text-xs">Cliente{value ? `: ${value.name}` : ""}</Label>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente" data-testid="rate-company-search" />
      {res.length > 0 && <ul className="absolute z-20 w-full rounded-control border border-line bg-white shadow-lg">{res.map((c) => (
        <li key={c.id}><button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => { onPick(c); setQ(""); }} data-testid={`rate-company-${c.id}`}>{c.name}</button></li>))}</ul>}
    </div>
  );
};

const RateDialog = ({ open, onOpenChange, card, onDone }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(card ? { ...card, travel_rate: card.travel_rate ?? "", valid_to: card.valid_to || "" } : { scope: "standard", hourly_rate: "", travel_rate: "", valid_from: today(), valid_to: "", active: true }); }, [open, card]);
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    const body = { hourly_rate: Number(f.hourly_rate), travel_rate: f.travel_rate === "" ? null : Number(f.travel_rate), valid_from: f.valid_from, valid_to: f.valid_to || null };
    try {
      if (card) await api.patch(`/rate-cards/${card.id}`, body);
      else await api.post("/rate-cards", { ...body, scope: f.scope, intervention_type: f.intervention_type || null, company_id: f.company?.id || null });
      toast.success("Tariffa salvata"); onOpenChange(false); onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const valid = Number(f.hourly_rate) > 0 && f.valid_from && (card || f.scope !== "tipologia" || f.intervention_type) && (card || f.scope !== "cliente" || f.company);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="sm:max-w-md" data-testid="rate-dialog">
      <DialogHeader><DialogTitle>{card ? "Modifica tariffa" : "Nuova tariffa"}</DialogTitle><DialogDescription>Priorità: tariffa del rapportino → cliente → tipologia → standard. Prezzi IVA esclusa.</DialogDescription></DialogHeader>
      <div className="space-y-3">
        {!card && <SelectField label="Ambito" value={f.scope} onChange={(v) => setF((x) => ({ ...x, scope: v }))} options={SCOPES} testId="rate-scope" />}
        {!card && f.scope === "tipologia" && <SelectField label="Tipologia" value={f.intervention_type} onChange={(v) => setF((x) => ({ ...x, intervention_type: v }))} options={ITV_TYPES} testId="rate-type" />}
        {!card && f.scope === "cliente" && <CompanyPick value={f.company} onPick={(c) => setF((x) => ({ ...x, company: c }))} />}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Tariffa oraria (€/h)</Label><Input type="number" min={0} step="0.01" value={f.hourly_rate ?? ""} onChange={s("hourly_rate")} data-testid="rate-hourly" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Trasferta (€/uscita)</Label><Input type="number" min={0} step="0.01" value={f.travel_rate ?? ""} onChange={s("travel_rate")} data-testid="rate-travel" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Valida dal</Label><Input type="date" value={f.valid_from || ""} onChange={s("valid_from")} data-testid="rate-from" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Valida fino al</Label><Input type="date" value={f.valid_to || ""} onChange={s("valid_to")} data-testid="rate-to" /></div>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button onClick={save} disabled={busy || !valid} data-testid="rate-dialog-save">Salva</Button></DialogFooter>
    </DialogContent></Dialog>
  );
};

export default function RateCardsPage() {
  const { can } = useAuth();
  const manage = can("rates:manage");
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const load = useCallback(() => api.get("/rate-cards").then((r) => setItems(r.data.items)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (c) => { try { await api.patch(`/rate-cards/${c.id}`, { active: !c.active }); load(); } catch (e) { toast.error(errorMessage(e)); } };
  const target = (c) => (c.scope === "standard" ? "Standard" : c.scope === "tipologia" ? typeLabel(c.intervention_type) : c.company_name);
  return (
    <div data-testid="rate-cards-page">
      <PageHeader overline="Configurazione" title="Tariffe interventi" description="Tariffe orarie e di trasferta usate per valorizzare i rapportini. Senza una tariffa applicabile la conferma economica è bloccata."
        actions={manage && <Button className="gap-2" onClick={() => setEdit(null)} data-testid="rate-new"><Plus />Nuova tariffa</Button>} />
      <Panel className="p-4 md:p-5">
        {items && !items.length && <p className="py-6 text-center text-sm text-slate-500" data-testid="rate-empty">Nessuna tariffa configurata.</p>}
        <ul className="divide-y divide-line">{(items || []).map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`rate-row-${c.id}`}>
            <div className="min-w-0 flex-1"><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{SCOPES.find((x) => x.key === c.scope)?.label}</p>
              <p className="text-sm font-semibold text-ink">{target(c)}</p><p className="text-xs text-slate-500">dal {c.valid_from}{c.valid_to ? ` al ${c.valid_to}` : ""}</p></div>
            <span className="text-sm text-ink">{fmtEur(c.hourly_rate)}/h</span><span className="text-sm text-slate-600">{c.travel_rate != null ? `${fmtEur(c.travel_rate)}/uscita` : "trasferta —"}</span>
            <StatusBadge tone={c.active ? "green" : "neutral"}>{c.active ? "Attiva" : "Disattiva"}</StatusBadge>
            {manage && <><Button size="sm" variant="outline" onClick={() => setEdit(c)} data-testid={`rate-edit-${c.id}`}>Modifica</Button>
              <Button size="sm" variant="ghost" onClick={() => toggle(c)} data-testid={`rate-toggle-${c.id}`}>{c.active ? "Disattiva" : "Attiva"}</Button></>}
          </li>))}</ul>
      </Panel>
      <RateDialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)} card={edit} onDone={load} />
    </div>
  );
}
